import {
  ArchiveRestore,
  ArrowDown,
  ChevronDown,
  Download,
  Eye,
  FileSignature,
  FolderPlus,
  History,
  LayoutGrid,
  List,
  Lock,
  MoreVertical,
  Move,
  Pencil,
  Search,
  Trash2,
  Upload,
  UploadCloud,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/common/loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  bulkDelete,
  bulkDownload,
  bulkMove,
  createFolder,
  deleteDocument,
  deleteFolder,
  listClientDocuments,
  moveDocument,
  openDocument,
  purgeDocument,
  renameDocument,
  renameFolder,
  restoreDocument,
  uploadDocument,
} from "@/features/documents/api";
import { FolderTree } from "@/features/documents/components/folder-tree";
import { SendESignDialog } from "@/features/esignatures/components/send-esign-dialog";
import { MoveDialog } from "@/features/documents/components/move-dialog";
import { UploadDialog } from "@/features/documents/components/upload-dialog";
import { VersionHistoryDialog } from "@/features/documents/components/version-history-dialog";
import { useClientArchive } from "@/features/documents/hooks/use-client-archive";
import type { ClientDocument, DocumentFolder } from "@/features/documents/types";
import { useActiveClient } from "@/layouts/client-layout";
import { cn } from "@/lib/utils";

type StatusLabel = "Draft" | "Verified" | "Needs Review" | "Signed";
type DocumentKind = "pdf" | "image" | "spreadsheet";

/** Coloured file-type tile (PDF/DOC/XLS/IMG) for a document row, per the mockup. */
function fileTile(doc: ClientDocument): { label: string; cls: string } {
  const ext = (doc.name.split(".").pop() || "").toLowerCase();
  if (/^(xlsx?|csv)$/.test(ext)) return { label: "XLS", cls: "bg-success-soft text-success" };
  if (/^(png|jpe?g|gif|webp)$/.test(ext)) return { label: "IMG", cls: "bg-accent text-info" };
  if (/^docx?$/.test(ext)) return { label: "DOC", cls: "bg-accent text-info" };
  if (ext === "pdf") return { label: "PDF", cls: "bg-warning-soft text-warning" };
  return { label: (ext || "FILE").slice(0, 4).toUpperCase(), cls: "bg-muted text-muted-foreground" };
}

// Status pill: a coloured dot + label. Draft is neutral-gray, signed/verified
// green, needs-review amber — matching the mockup's muted pill treatment.
const statusDotClass: Record<StatusLabel, string> = {
  Draft: "bg-muted-foreground",
  Verified: "bg-success",
  "Needs Review": "bg-warning",
  Signed: "bg-success",
};

const STATUS_LABEL: Record<ClientDocument["status"], StatusLabel> = {
  DRAFT: "Draft",
  PENDING_SIGNATURE: "Needs Review",
  SIGNED: "Signed",
  VERIFIED: "Verified",
};

// Proforma-Invoice approval outcome → dot colour. When a document is a PI its
// approval status replaces the generic signature status in the STATUS column.
const PROFORMA_DOT: Record<string, string> = {
  PENDING_APPROVAL: "bg-warning",
  APPROVED: "bg-warning",
  RETURNED: "bg-destructive",
  ACCEPTED: "bg-success",
  DECLINED: "bg-destructive",
};

const STATUS_FILTERS: { value: ClientDocument["status"]; label: StatusLabel }[] = [
  { value: "DRAFT", label: "Draft" },
  { value: "PENDING_SIGNATURE", label: "Needs Review" },
  { value: "SIGNED", label: "Signed" },
  { value: "VERIFIED", label: "Verified" },
];

const KIND_FILTERS: { value: DocumentKind; label: string }[] = [
  { value: "pdf", label: "PDF / Docs" },
  { value: "image", label: "Images" },
  { value: "spreadsheet", label: "Spreadsheets" },
];

type SortKey = "name" | "type" | "size" | "date" | "status";

function fileKind(doc: ClientDocument): DocumentKind {
  const t = `${doc.contentType} ${doc.name}`.toLowerCase();
  if (t.includes("image") || /\.(png|jpe?g|gif|webp)$/.test(t)) return "image";
  if (t.includes("sheet") || t.includes("excel") || t.includes("csv") || /\.(xlsx?|csv)$/.test(t))
    return "spreadsheet";
  return "pdf";
}

function fileTypeLabel(doc: ClientDocument): string {
  const ext = doc.name.includes(".") ? doc.name.split(".").pop() : "";
  return (ext || doc.contentType.split("/").pop() || "FILE").toUpperCase();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const STORAGE_LIMIT = 5 * 1024 * 1024 * 1024; // 5 GB (UI figure — no server quota yet)

export default function ClientDocumentsPage() {
  const { client } = useActiveClient();
  const { folders, documents, loading, error, refetch } = useClientArchive(client.id);

  const [view, setView] = useState<"active" | "deleted">("active");
  const [deletedDocs, setDeletedDocs] = useState<ClientDocument[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [folderSearch, setFolderSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ClientDocument["status"][]>([]);
  const [kindFilter, setKindFilter] = useState<DocumentKind[]>([]);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "date",
    dir: "desc",
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createParent, setCreateParent] = useState<DocumentFolder | null | undefined>(undefined);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameFolderTarget, setRenameFolderTarget] = useState<DocumentFolder | null>(null);
  const [renameFolderValue, setRenameFolderValue] = useState("");
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<DocumentFolder | null>(null);
  const [renameDocTarget, setRenameDocTarget] = useState<ClientDocument | null>(null);
  const [renameDocValue, setRenameDocValue] = useState("");
  const [moveState, setMoveState] = useState<{ open: boolean; ids: string[] }>({
    open: false,
    ids: [],
  });
  const [versionsDoc, setVersionsDoc] = useState<ClientDocument | null>(null);
  const [deleteDocTarget, setDeleteDocTarget] = useState<ClientDocument | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState<ClientDocument | null>(null);
  const [sendEsignDoc, setSendEsignDoc] = useState<ClientDocument | null>(null);
  // Guards against an older deleted-docs fetch resolving after a newer one and
  // clobbering the list (e.g. rapid tab flips or refresh-after-restore/purge).
  const deletedReqRef = useRef(0);

  const loadDeleted = () => {
    const reqId = ++deletedReqRef.current;
    setDeletedLoading(true);
    listClientDocuments(client.id, { deleted: true })
      .then((docs) => {
        if (reqId === deletedReqRef.current) setDeletedDocs(docs);
      })
      .catch(() => {
        if (reqId === deletedReqRef.current) toast.error("Couldn't load deleted documents.");
      })
      .finally(() => {
        if (reqId === deletedReqRef.current) setDeletedLoading(false);
      });
  };

  useEffect(() => {
    if (view === "deleted") loadDeleted();
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const sourceDocs = view === "deleted" ? deletedDocs : documents;

  const visibleDocs = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = sourceDocs.filter((d) => {
      if (view === "active" && selectedFolderId && d.folder !== selectedFolderId) return false;
      if (term && !d.name.toLowerCase().includes(term)) return false;
      if (statusFilter.length && !statusFilter.includes(d.status)) return false;
      if (kindFilter.length && !kindFilter.includes(fileKind(d))) return false;
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    return rows.sort((a, b) => {
      switch (sort.key) {
        case "name":
          return a.name.localeCompare(b.name) * dir;
        case "type":
          return fileTypeLabel(a).localeCompare(fileTypeLabel(b)) * dir;
        case "size":
          return (a.sizeBytes - b.sizeBytes) * dir;
        case "status":
          return a.status.localeCompare(b.status) * dir;
        default:
          return (
            (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
          );
      }
    });
  }, [sourceDocs, view, selectedFolderId, search, statusFilter, kindFilter, sort]);

  const pendingCount = documents.filter((d) => d.status === "PENDING_SIGNATURE").length;
  const storageUsed = documents.reduce((sum, d) => sum + d.sizeBytes, 0);
  const storagePct = Math.min(100, Math.round((storageUsed / STORAGE_LIMIT) * 100));

  const selectedList = visibleDocs.filter((d) => selectedIds.has(d.id));
  const allSelected = visibleDocs.length > 0 && selectedList.length === visibleDocs.length;

  function toggleSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(visibleDocs.map((d) => d.id)));
  }

  function refreshAll() {
    refetch();
    if (view === "deleted") loadDeleted();
    setSelectedIds(new Set());
  }

  async function handleCreateFolder() {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await createFolder(client.id, name, createParent ? createParent.id : selectedFolderId);
      toast.success("Folder created");
      setNewFolderName("");
      setCreateParent(undefined);
      refetch();
    } catch (err) {
      toast.error(errText(err, "Couldn't create folder."));
    }
  }

  async function handleRenameFolder() {
    if (!renameFolderTarget) return;
    const name = renameFolderValue.trim();
    if (!name) return;
    try {
      await renameFolder(renameFolderTarget.id, name);
      toast.success("Folder renamed");
      setRenameFolderTarget(null);
      refetch();
    } catch (err) {
      toast.error(errText(err, "Couldn't rename folder."));
    }
  }

  async function handleDeleteFolder() {
    if (!deleteFolderTarget) return;
    try {
      await deleteFolder(deleteFolderTarget.id);
      toast.success("Folder deleted");
      if (selectedFolderId === deleteFolderTarget.id) setSelectedFolderId(null);
      setDeleteFolderTarget(null);
      refetch();
    } catch (err) {
      toast.error(errText(err, "Couldn't delete folder."));
    }
  }

  async function handleRenameDoc() {
    if (!renameDocTarget) return;
    const name = renameDocValue.trim();
    if (!name) return;
    try {
      await renameDocument(renameDocTarget.id, name);
      toast.success("Document renamed");
      setRenameDocTarget(null);
      refetch();
    } catch (err) {
      toast.error(errText(err, "Couldn't rename document."));
    }
  }

  async function handleMove(folderId: string | null) {
    const ids = moveState.ids;
    try {
      if (ids.length === 1 && ids[0]) await moveDocument(ids[0], folderId);
      else await bulkMove(ids, folderId);
      toast.success(ids.length === 1 ? "Document moved" : `${ids.length} documents moved`);
      refreshAll();
    } catch (err) {
      toast.error(errText(err, "Couldn't move."));
    }
  }

  async function confirmDeleteDoc() {
    if (!deleteDocTarget) return;
    try {
      await deleteDocument(deleteDocTarget.id);
      toast.success("Moved to Recently Deleted");
      refreshAll();
    } catch (err) {
      toast.error(errText(err, "Couldn't delete document."));
    } finally {
      setDeleteDocTarget(null);
    }
  }

  async function confirmBulkDelete() {
    try {
      const n = await bulkDelete([...selectedIds]);
      toast.success(`${n} document${n === 1 ? "" : "s"} moved to Recently Deleted`);
      refreshAll();
    } catch (err) {
      toast.error(errText(err, "Couldn't delete documents."));
    } finally {
      setBulkDeleteOpen(false);
    }
  }

  async function handleBulkDownload() {
    try {
      const result = await bulkDownload([...selectedIds]);
      if (result.queued) toast.success(result.message ?? "Preparing your download…");
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(errText(err, "Couldn't download."));
    }
  }

  async function handleRestore(doc: ClientDocument) {
    try {
      await restoreDocument(doc.id);
      toast.success("Document restored");
      refreshAll();
    } catch (err) {
      toast.error(errText(err, "Couldn't restore document."));
    }
  }

  async function confirmPurge() {
    if (!purgeTarget) return;
    try {
      await purgeDocument(purgeTarget.id);
      toast.success("Permanently deleted");
      refreshAll();
    } catch (err) {
      toast.error(errText(err, "Couldn't delete permanently."));
    } finally {
      setPurgeTarget(null);
    }
  }

  const folderNameById = useMemo(() => new Map(folders.map((f) => [f.id, f.name])), [folders]);
  const [dragOver, setDragOver] = useState(false);

  async function handleDropFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (!list.length) return;
    // Upload each file independently so one rejection (AV/size/type) doesn't
    // abort the rest; report partial success like the UploadDialog does.
    const results = await Promise.allSettled(
      list.map((f) => uploadDocument(client.id, f, { folderId: selectedFolderId })),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok) {
      toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
      refreshAll();
    }
    if (failed) toast.error(`${failed} file${failed === 1 ? "" : "s"} couldn't be uploaded.`);
  }

  const colCount = 7;
  const busy = view === "active" ? loading : deletedLoading;

  return (
    <div className="flex flex-col gap-6">
      {/* Title + actions */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div className="flex flex-col gap-1">
          <h1 className="text-[30px] leading-tight font-semibold tracking-tight">Documents</h1>
          <p className="text-muted-foreground flex flex-wrap items-center gap-x-3.5 gap-y-1 text-sm">
            <span>
              <strong className="text-foreground font-semibold">{documents.length}</strong> files
            </span>
            <span>
              <strong className="text-foreground font-semibold">{pendingCount}</strong> pending
              signature
            </span>
            <span className="text-success flex items-center gap-1.5">
              <Lock className="size-3.5" /> Encrypted at rest
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div
            className="mr-1 flex min-w-[170px] flex-col gap-1.5"
            aria-label={`Storage used: ${formatBytes(storageUsed)} of 5 GB`}
          >
            <span className="text-muted-foreground flex justify-between text-xs">
              <span>Storage</span>
              <span className="tabular-nums">
                <strong className="text-foreground font-semibold">{formatBytes(storageUsed)}</strong>{" "}
                / 5 GB
              </span>
            </span>
            <span className="bg-border block h-[5px] overflow-hidden rounded-full">
              <span
                className="bg-info block h-full rounded-full"
                style={{ width: `${Math.max(3, storagePct)}%` }}
              />
            </span>
          </div>
          <Button
            variant="outline"
            className="h-[42px]"
            onClick={() => {
              setCreateParent(null);
              setNewFolderName("");
            }}
          >
            <FolderPlus /> New folder
          </Button>
          <Button variant="cta" className="h-[42px]" onClick={() => setUploadOpen(true)}>
            <Upload /> Upload
          </Button>
        </div>
      </div>

      {/* Folders + files */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
        {/* Folders aside */}
        <Card className="gap-3 p-[18px] lg:w-[300px] lg:shrink-0">
          <div className="bg-background flex h-9 items-center gap-2 rounded-[11px] px-3">
            <Search className="text-muted-foreground size-[15px]" />
            <input
              type="search"
              placeholder="Search folders…"
              className="min-w-0 flex-1 border-0 bg-transparent text-[13px] outline-none"
              value={folderSearch}
              onChange={(e) => setFolderSearch(e.target.value)}
            />
          </div>
          <nav className="flex flex-1 flex-col gap-0.5">
            <button
              type="button"
              onClick={() => {
                setView("active");
                setSelectedFolderId(null);
              }}
              className={cn(
                "flex h-10 items-center gap-2.5 rounded-[11px] px-3 text-left text-sm font-medium transition-colors",
                view === "active" && selectedFolderId === null
                  ? "bg-accent text-info font-semibold"
                  : "text-foreground hover:bg-background",
              )}
            >
              <List className="size-4" />
              <span className="flex-grow">All documents</span>
              <span className="text-xs tabular-nums">{documents.length}</span>
            </button>
            <span className="text-muted-foreground px-3 pt-3 pb-1 text-[11px] font-semibold tracking-[0.1em] uppercase">
              Folders
            </span>
            <FolderTree
              folders={folders}
              selectedFolderId={view === "active" ? selectedFolderId : "__none__"}
              filter={folderSearch}
              onSelect={(fid) => {
                setView("active");
                setSelectedFolderId(fid);
              }}
              onNewSubfolder={(parent) => {
                setCreateParent(parent);
                setNewFolderName("");
              }}
              onRename={(folder) => {
                setRenameFolderTarget(folder);
                setRenameFolderValue(folder.name);
              }}
              onDelete={setDeleteFolderTarget}
            />
            <div className="flex-grow" />
            <button
              type="button"
              onClick={() => setView("deleted")}
              className={cn(
                "mt-1.5 flex h-10 items-center gap-2.5 rounded-[11px] border-t px-3 pt-1.5 text-left text-sm font-medium transition-colors",
                view === "deleted"
                  ? "bg-accent text-info font-semibold"
                  : "text-muted-foreground hover:bg-background",
              )}
            >
              <Trash2 className="size-4" />
              <span className="flex-grow">Recently deleted</span>
              <span className="text-xs tabular-nums">{view === "deleted" ? deletedDocs.length : ""}</span>
            </button>
          </nav>
        </Card>

        {/* Files panel */}
        <Card className="min-w-0 flex-1 gap-0 overflow-hidden p-0">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2.5 border-b px-5 py-4">
            <div className="bg-background flex h-10 max-w-[320px] min-w-0 flex-1 items-center gap-2 rounded-sm px-3.5">
              <Search className="text-muted-foreground size-4" />
              <input
                type="search"
                placeholder="Search in all documents…"
                className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-10">
                  Type{kindFilter.length ? ` (${kindFilter.length})` : ""} <ChevronDown className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {KIND_FILTERS.map((k) => (
                  <DropdownMenuCheckboxItem
                    key={k.value}
                    checked={kindFilter.includes(k.value)}
                    onCheckedChange={() =>
                      setKindFilter((prev) =>
                        prev.includes(k.value) ? prev.filter((v) => v !== k.value) : [...prev, k.value],
                      )
                    }
                  >
                    {k.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-10">
                  Status{statusFilter.length ? ` (${statusFilter.length})` : ""} <ChevronDown className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {STATUS_FILTERS.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s.value}
                    checked={statusFilter.includes(s.value)}
                    onCheckedChange={() =>
                      setStatusFilter((prev) =>
                        prev.includes(s.value) ? prev.filter((v) => v !== s.value) : [...prev, s.value],
                      )
                    }
                  >
                    {s.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex-grow" />
            <button
              type="button"
              onClick={() => toggleSort("date")}
              className="bg-accent text-info flex h-10 items-center gap-2 rounded-sm px-3.5 text-[13px] font-semibold"
            >
              {sort.key === "date" && sort.dir === "asc" ? "Oldest first" : "Newest first"}
              <ArrowDown
                className={cn(
                  "size-3.5 transition-transform",
                  sort.key === "date" && sort.dir === "asc" && "rotate-180",
                )}
              />
            </button>
            <div role="group" aria-label="View" className="bg-muted flex gap-0.5 rounded-[11px] p-[3px]">
              <span className="bg-card text-foreground shadow-card flex size-8 items-center justify-center rounded-lg">
                <List className="size-[15px]" />
              </span>
              <button
                type="button"
                aria-label="Grid view"
                title="Grid view coming soon"
                disabled
                className="text-muted-foreground flex size-8 items-center justify-center rounded-lg opacity-60"
              >
                <LayoutGrid className="size-[15px]" />
              </button>
            </div>
          </div>

          {/* Selection bar */}
          {view === "active" && selectedIds.size > 0 && (
            <div className="bg-accent/50 flex flex-wrap items-center gap-2 border-b px-5 py-2.5 text-sm">
              <span className="text-foreground font-medium">{selectedIds.size} selected</span>
              <div className="flex-grow" />
              <Button variant="ghost" size="sm" onClick={handleBulkDownload}>
                <Download /> Download
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMoveState({ open: true, ids: [...selectedIds] })}
              >
                <Move /> Move
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 /> Delete
              </Button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 [&>th]:text-muted-foreground [&>th]:text-xs [&>th]:font-semibold [&>th]:tracking-wide [&>th]:uppercase">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all documents"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Folder</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {busy ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="py-10">
                      <Loader />
                    </TableCell>
                  </TableRow>
                ) : view === "active" && error ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="text-destructive py-8 text-center">
                      {error}
                    </TableCell>
                  </TableRow>
                ) : visibleDocs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="text-muted-foreground py-8 text-center">
                      {view === "deleted" ? "Recently deleted is empty." : "No documents here yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleDocs.map((doc) => {
                    const tile = fileTile(doc);
                    // A Proforma Invoice shows its approval outcome; other docs
                    // show the signature-workflow status.
                    const status = doc.proformaStatusDisplay ?? STATUS_LABEL[doc.status];
                    const statusDot = doc.proformaStatus
                      ? (PROFORMA_DOT[doc.proformaStatus] ?? "bg-muted-foreground")
                      : statusDotClass[STATUS_LABEL[doc.status]];
                    return (
                      <TableRow
                        key={doc.id}
                        className="group"
                        data-state={selectedIds.has(doc.id) ? "selected" : undefined}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(doc.id)}
                            onCheckedChange={() => toggleSelect(doc.id)}
                            aria-label={`Select ${doc.name}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <span
                              className={cn(
                                "font-heading flex size-[38px] shrink-0 items-center justify-center rounded-[11px] text-[10px] font-bold tracking-wide",
                                tile.cls,
                              )}
                            >
                              {tile.label}
                            </span>
                            <span className="flex min-w-0 flex-col leading-tight">
                              <span className="flex items-center gap-2">
                                <span
                                  className="text-foreground max-w-[340px] truncate font-semibold"
                                  title={doc.name}
                                >
                                  {doc.name}
                                </span>
                                {doc.version > 1 && (
                                  <span className="bg-accent text-info rounded-full px-1.5 py-0.5 text-[11px] font-semibold">
                                    v{doc.version}
                                  </span>
                                )}
                              </span>
                              <span className="text-muted-foreground text-xs">
                                {doc.formNumber ? "IRS form · " : ""}Uploaded by{" "}
                                {doc.uploadedByName || "—"}
                              </span>
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {folderNameById.get(doc.folder ?? "") ?? "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right tabular-nums">
                          {formatBytes(doc.sizeBytes)}
                        </TableCell>
                        <TableCell className="text-muted-foreground tabular-nums">
                          {new Date(doc.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </TableCell>
                        <TableCell>
                          <span className="bg-muted text-muted-foreground inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold">
                            <span className={cn("size-1.5 rounded-full", statusDot)} />
                            {status}
                          </span>
                        </TableCell>
                        <TableCell>
                          {view === "deleted" ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`Restore ${doc.name}`}
                                onClick={() => handleRestore(doc)}
                              >
                                <ArchiveRestore className="size-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive"
                                aria-label={`Delete ${doc.name} permanently`}
                                onClick={() => setPurgeTarget(doc)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [&:has([data-state=open])]:opacity-100">
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`Preview ${doc.name}`}
                                onClick={() =>
                                  openDocument(doc.id).catch(() =>
                                    toast.error("Couldn't open document."),
                                  )
                                }
                              >
                                <Eye className="size-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`Download ${doc.name}`}
                                onClick={() =>
                                  openDocument(doc.id).catch(() =>
                                    toast.error("Couldn't open document."),
                                  )
                                }
                              >
                                <Download className="size-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={`More actions for ${doc.name}`}
                                  >
                                    <MoreVertical className="size-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setRenameDocTarget(doc);
                                      setRenameDocValue(doc.name);
                                    }}
                                  >
                                    <Pencil className="size-4" /> Rename
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => setMoveState({ open: true, ids: [doc.id] })}
                                  >
                                    <Move className="size-4" /> Move to folder
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setVersionsDoc(doc)}>
                                    <History className="size-4" /> Versions
                                  </DropdownMenuItem>
                                  {doc.contentType === "application/pdf" && (
                                    <DropdownMenuItem onClick={() => setSendEsignDoc(doc)}>
                                      <FileSignature className="size-4" /> Send for e-signature
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => setDeleteDocTarget(doc)}
                                  >
                                    <Trash2 className="size-4" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Drop zone */}
          {view === "active" && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleDropFiles(e.dataTransfer.files);
              }}
              className={cn(
                "m-5 flex flex-wrap items-center justify-center gap-3.5 rounded-lg border border-dashed px-6 py-6 text-center transition-colors",
                dragOver ? "border-info bg-accent/40" : "border-input bg-surface-hover",
              )}
            >
              <UploadCloud className="text-info size-9" />
              <span className="flex flex-col gap-0.5 leading-snug">
                <span className="font-semibold">
                  Drop files here to upload
                  {selectedFolderId ? ` to ${folderNameById.get(selectedFolderId) ?? "this folder"}` : ""}
                </span>
                <span className="text-muted-foreground text-[13px]">
                  PDF, DOCX, XLSX, JPG · up to 25 MB each ·{" "}
                  <button
                    type="button"
                    className="text-info font-semibold hover:underline"
                    onClick={() => setUploadOpen(true)}
                  >
                    browse files
                  </button>
                </span>
              </span>
            </div>
          )}
        </Card>
      </div>

      {/* Upload modal */}
      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        clientId={client.id}
        folders={folders}
        defaultFolderId={selectedFolderId}
        onUploaded={refreshAll}
      />

      {/* Create folder dialog */}
      <Dialog open={createParent !== undefined} onOpenChange={(open) => !open && setCreateParent(undefined)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {createParent ? `New folder in “${createParent.name}”` : "New Folder"}
            </DialogTitle>
          </DialogHeader>
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateFolder();
            }}
          >
            <Label htmlFor="folder-name">Folder name</Label>
            <Input
              id="folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="e.g. 2023 Returns"
              autoFocus
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateParent(undefined)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newFolderName.trim()}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={renameFolderTarget !== null} onOpenChange={(open) => !open && setRenameFolderTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
          </DialogHeader>
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              handleRenameFolder();
            }}
          >
            <Label htmlFor="rename-folder">Folder name</Label>
            <Input
              id="rename-folder"
              value={renameFolderValue}
              onChange={(e) => setRenameFolderValue(e.target.value)}
              autoFocus
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameFolderTarget(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!renameFolderValue.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename document dialog */}
      <Dialog open={renameDocTarget !== null} onOpenChange={(open) => !open && setRenameDocTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename document</DialogTitle>
          </DialogHeader>
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              handleRenameDoc();
            }}
          >
            <Label htmlFor="rename-doc">Name</Label>
            <Input
              id="rename-doc"
              value={renameDocValue}
              onChange={(e) => setRenameDocValue(e.target.value)}
              autoFocus
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameDocTarget(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!renameDocValue.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Move dialog (single or bulk) */}
      <MoveDialog
        open={moveState.open}
        onOpenChange={(open) => setMoveState((s) => ({ ...s, open }))}
        folders={folders}
        count={moveState.ids.length}
        onConfirm={handleMove}
      />

      {/* Version history */}
      <VersionHistoryDialog document={versionsDoc} onOpenChange={(open) => !open && setVersionsDoc(null)} />

      {/* Send for e-signature */}
      <SendESignDialog
        open={sendEsignDoc !== null}
        onOpenChange={(open) => !open && setSendEsignDoc(null)}
        client={client}
        document={sendEsignDoc ? { id: sendEsignDoc.id, name: sendEsignDoc.name } : null}
        onSent={refetch}
      />

      {/* Delete folder confirm */}
      <AlertDialog open={deleteFolderTarget !== null} onOpenChange={(open) => !open && setDeleteFolderTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete folder?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteFolderTarget?.name}” must be empty. Its contents (if any) must be moved or
              deleted first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFolder}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete document confirm (soft-delete) */}
      <AlertDialog open={deleteDocTarget !== null} onOpenChange={(open) => !open && setDeleteDocTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to Recently Deleted?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDocTarget?.name} will be moved to Recently Deleted. You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDoc}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} documents?</AlertDialogTitle>
            <AlertDialogDescription>
              They will be moved to Recently Deleted and can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Purge confirm */}
      <AlertDialog open={purgeTarget !== null} onOpenChange={(open) => !open && setPurgeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              {purgeTarget?.name} will be permanently deleted. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPurge}>Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function errText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
